import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "@/api/auth";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

export default function Callback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { refetchUser } = useAuth();
  const hasExecuted = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (hasExecuted.current) return;
      hasExecuted.current = true;

      const code = searchParams.get("code");
      const error = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      if (error) {
        toast({
          title: "Authentication Error",
          description:
            errorDescription || "Failed to authenticate. Please try again.",
          variant: "destructive",
        });
        navigate("/login", { replace: true });
        return;
      }

      if (!code) {
        toast({
          title: "Invalid Request",
          description: "Authorization code is missing. Please try again.",
          variant: "destructive",
        });
        navigate("/login", { replace: true });
        return;
      }

      try {
        await authService.callback(code);

        // Fetch user data after successful token exchange
        await refetchUser();

        const redirectPath =
          sessionStorage.getItem("auth_redirect") || "/dashboard";
        sessionStorage.removeItem("auth_redirect");

        toast({
          title: "Welcome!",
          description: "You have been successfully logged in.",
        });

        navigate(redirectPath, { replace: true });
      } catch (error) {
        toast({
          title: "Login Failed",
          description:
            error instanceof Error
              ? error.message
              : "Failed to complete login. Please try again.",
          variant: "destructive",
        });
        navigate("/login", { replace: true });
      }
    };

    handleCallback();
  }, [navigate, searchParams, toast, refetchUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing your login...</p>
      </div>
    </div>
  );
}
